import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import RetryError from '../ui/RetryError'

// 기본 정책: 조직 트리는 "대부분 작다"는 전제 하에 기본 펼침을 사용한다.
// - 그래도 안전을 위해 depth/node 가드를 둔다.
const DEFAULT_EXPAND_ALL = true
const MAX_EXPAND_DEPTH = 10
const MAX_EXPAND_NODES = 800

export default function OrgTree({ onSelect, selectedId, searchQuery }) {
    const [tree, setTree] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [expanded, setExpanded] = useState(new Set())
    const [loadingNodes, setLoadingNodes] = useState(new Set())
    const [pageInfo, setPageInfo] = useState({})

    const expandSeqRef = useRef(0)

    const normalizeListPayload = (res) => {
        const data = res?.data?.data || res?.data
        if (Array.isArray(data)) return data
        return data?.items || []
    }

    const fetchChildren = useCallback(async (parentId) => {
        const res = await api.get(`/orgs/${parentId}/children`)
        const children = normalizeListPayload(res)
        return Array.isArray(children) ? children : []
    }, [])

    const buildTreeFully = useCallback(async (roots) => {
        let nodeCount = 0
        const expandedIds = []

        const enrich = async (node, depth) => {
            if (!node || !node.id) return node
            if (!node.hasChildren) return node
            if (depth >= MAX_EXPAND_DEPTH) return node
            if (nodeCount >= MAX_EXPAND_NODES) return node

            expandedIds.push(node.id)
            const children = await fetchChildren(node.id)
            nodeCount += children.length

            const enrichedChildren = []
            for (const c of children) {
                // eslint-disable-next-line no-await-in-loop
                enrichedChildren.push(await enrich(c, depth + 1))
            }
            return { ...node, children: enrichedChildren, hasLoaded: true, hasChildren: enrichedChildren.length > 0 }
        }

        const out = []
        for (const r of roots) {
            // eslint-disable-next-line no-await-in-loop
            out.push(await enrich(r, 0))
        }
        return { tree: out, expandedIds }
    }, [fetchChildren])

    const loadRoot = useCallback(async () => {
        setLoading(true)
        setError(null)
        const seq = (expandSeqRef.current += 1)
        try {
            const params = { page: 0, size: 10 }
            if (searchQuery) params.q = searchQuery
            const res = await api.get('/orgs/tree', { params })
            const data = res.data?.data || res.data
            const items = Array.isArray(data) ? data : data.items || []
            // 루트만 표시: parentOrgId가 존재하지 않는 노드만
            const roots = items.filter(n => !n.parentOrgId)

            if (DEFAULT_EXPAND_ALL) {
                try {
                    const built = await buildTreeFully(roots)
                    // 최신 요청만 반영
                    if (seq === expandSeqRef.current) {
                        setTree(built.tree)
                        setExpanded(new Set(built.expandedIds))
                    }
                } catch {
                    // fallback
                    if (seq === expandSeqRef.current) {
                        setTree(roots)
                        setExpanded(new Set(roots.map(r => r.id)))
                    }
                }
            } else {
                setTree(roots)
            }
            setPageInfo({ hasNext: data.hasNext || false, totalPages: data.totalPages || 1, currentPage: 0 })
        } catch (e) {
            setError('조직 트리를 불러오지 못했습니다')
        } finally {
            setLoading(false)
        }
    }, [searchQuery, buildTreeFully])

    useEffect(() => {
        let ignore = false
            ; (async () => { if (!ignore) await loadRoot() })()
        return () => { ignore = true }
    }, [searchQuery, loadRoot])

    const updateTreeWithChildren = React.useCallback((nodes, parentId, children) => {
        return nodes.map(node => {
            if (node.id === parentId) {
                return { ...node, children, hasChildren: children.length > 0, hasLoaded: true }
            }
            if (node.children) {
                return { ...node, children: updateTreeWithChildren(node.children, parentId, children) }
            }
            return node
        })
    }, [])

    const loadChildren = useCallback(async (parentId) => {
        if (loadingNodes.has(parentId)) return
        setLoadingNodes(prev => new Set(prev).add(parentId))
        try {
            const res = await api.get(`/orgs/${parentId}/children`)
            const children = normalizeListPayload(res)
            setTree(prev => updateTreeWithChildren(prev, parentId, children))
        } catch (e) {
            console.error('Failed to load children:', e)
        } finally {
            setLoadingNodes(prev => { const next = new Set(prev); next.delete(parentId); return next })
        }
    }, [loadingNodes, updateTreeWithChildren])

    const findNodeInTree = React.useCallback((nodes, targetId) => {
        for (const node of nodes) {
            if (node.id === targetId) return node
            if (node.children) {
                const found = findNodeInTree(node.children, targetId)
                if (found) return found
            }
        }
        return null
    }, [])

    const toggleExpanded = useCallback((nodeId) => {
        setExpanded(prev => {
            const next = new Set(prev)
            if (next.has(nodeId)) {
                next.delete(nodeId)
            } else {
                next.add(nodeId)
                const node = findNodeInTree(tree, nodeId)
                if (node && !node.children && !node.hasLoaded) {
                    loadChildren(nodeId)
                }
            }
            return next
        })
    }, [tree, loadChildren, findNodeInTree])

    const loadMoreRoot = useCallback(async () => {
        if (pageInfo.hasNext === false) return
        setLoading(true)
        const seq = (expandSeqRef.current += 1)
        try {
            const params = { page: pageInfo.currentPage + 1, size: 10 }
            if (searchQuery) params.q = searchQuery
            const res = await api.get('/orgs/tree', { params })
            const data = res.data?.data || res.data
            const newItems = (Array.isArray(data) ? data : data.items || []).filter(n => !n.parentOrgId)

            if (DEFAULT_EXPAND_ALL) {
                const built = await buildTreeFully(newItems)
                if (seq === expandSeqRef.current) {
                    setTree(prev => [...prev, ...built.tree])
                    setExpanded(prev => {
                        const next = new Set(prev)
                        for (const id of built.expandedIds) next.add(id)
                        return next
                    })
                }
            } else {
                setTree(prev => [...prev, ...newItems])
            }

            setPageInfo({ hasNext: data.hasNext || false, totalPages: data.totalPages || 1, currentPage: pageInfo.currentPage + 1 })
        } catch (e) {
            console.error('Failed to load more:', e)
        } finally {
            setLoading(false)
        }
    }, [pageInfo, searchQuery, buildTreeFully])

    const typeLabel = (t) => {
        if (!t) return '-'
        if (t === 'GA (일반 대리점)') return 'GA'
        return t
    }

    const childCountOf = (node) => {
        const count = node.childCount ?? node.childrenCount
        if (typeof count === 'number') return count
        if (Array.isArray(node.children)) return node.children.length
        return node.hasChildren ? '…' : 0
    }

    const renderNode = (node, level = 0) => {
        const isExpanded = expanded.has(node.id)
        const hasChildren = node.children || node.hasChildren
        const isLoading = loadingNodes.has(node.id)
        return (
            <li key={node.id} className="text-sm">
                <div className="flex items-center gap-1 py-1" style={{ paddingLeft: `${level * 16}px` }}>
                    {hasChildren && (
                        <button className="w-4 h-4 flex items-center justify-center text-xs hover:bg-slate-100 rounded" onClick={() => toggleExpanded(node.id)}>
                            {isLoading ? '⟳' : (isExpanded ? '▼' : '▶')}
                        </button>
                    )}
                    {!hasChildren && <div className="w-4" />}
                    <button className={`hover:underline text-left ${selectedId === node.id ? 'font-semibold text-brand-navy' : 'text-slate-700'}`} onClick={() => onSelect(node)}>
                        {node.name} <span className="text-slate-400">({typeLabel(node.type)})</span> <span className="text-slate-400">[{childCountOf(node)}]</span>
                    </button>
                </div>
                {isExpanded && node.children && (<ul className="space-y-1">{node.children.map(child => renderNode(child, level + 1))}</ul>)}
                {isExpanded && isLoading && (<div className="ml-6"><Loading label="로딩 중..." className="text-xs p-0" /></div>)}
            </li>
        )
    }

    if (loading && tree.length === 0) return <Loading />
    if (error) return <RetryError message={error} onRetry={loadRoot} />

    return (
        <div className="space-y-2">
            <ul className="space-y-1 max-h-80 overflow-auto">
                {tree.map(node => renderNode(node))}
                {tree.length === 0 && !loading && (<li className="text-slate-500 text-sm">표시할 조직이 없습니다</li>)}
            </ul>
            {pageInfo.hasNext && (
                <button className="w-full text-sm text-slate-600 hover:text-brand-navy py-2 border-t" onClick={loadMoreRoot} disabled={loading}>
                    {loading ? '로딩 중...' : '더 보기'}
                </button>
            )}
        </div>
    )
}
